import { useState, useEffect, useRef, useMemo } from 'react';
import debounce from 'lodash.debounce';

import { GroupedOption, SelectOption } from '~/shared/utils/types';

interface UseSearchFilterOptions {
  initialSelections: SelectOption[];
  onSelectionsChange: (selections: SelectOption[]) => void;
  searchFunction: (pattern: string) => Promise<GroupedOption[]>;
  filterGroup: string;
  filterGroupMatcher?: (group: string) => boolean;
  debounceMs?: number;
  defaultQuery?: string;
  isDisclosureOpen?: boolean;
}

export function useSearchFilter({
  initialSelections,
  onSelectionsChange,
  searchFunction,
  filterGroup,
  filterGroupMatcher,
  debounceMs = 300,
  defaultQuery,
  isDisclosureOpen,
}: UseSearchFilterOptions) {
  const matchesGroup = useMemo(
    () =>
      filterGroupMatcher ||
      ((group: string) =>
        group === filterGroup || (filterGroup === 'tags' && group.startsWith('tag:'))),
    [filterGroupMatcher, filterGroup]
  );

  const [searchResults, setSearchResults] = useState<GroupedOption[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [showResults, setShowResults] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const [isSelecting, setIsSelecting] = useState(false);
  const [localSelections, setLocalSelections] = useState<SelectOption[]>(initialSelections);

  const inputRef = useRef<HTMLInputElement>(null);
  const resultsRef = useRef<HTMLDivElement>(null);
  const mouseDownOnResults = useRef(false);

  useEffect(() => {
    setLocalSelections(initialSelections);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialSelections]);

  const debouncedSearch = useRef(
    debounce((pattern: string, searchId: number) => {
      searchFunction(pattern)
        .then((results) => {
          if (searchIdRef.current !== searchId) {
            return;
          }

          if (!results) {
            setIsLoading(false);
            return;
          }

          // Check if there are selected items not in the search results and add them
          const currentFilteredSelections = localSelections.filter((s) => matchesGroup(s.group));

          const selectedNotInResults = currentFilteredSelections.filter(
            (selected) =>
              !results.some((group) => group.options.some((opt) => opt.id === selected.id))
          );

          if (selectedNotInResults.length > 0) {
            // Show selected items that are not in search results at the top
            setSearchResults([{ label: '', options: selectedNotInResults }, ...results]);
          } else {
            // Just show the search results
            setSearchResults(results);
          }

          setFocusedIndex(-1);
          setIsLoading(false);
        })
        .catch(() => {
          setSearchResults([]);
          setIsLoading(false);
        });
    }, debounceMs)
  ).current;

  const searchIdRef = useRef(0);

  // Flattened results for keyboard navigation
  const flattenedResults = useMemo(
    () => searchResults.flatMap((group) => group.options),
    [searchResults]
  );

  const selectedIdSet = useMemo(() => new Set(localSelections.map((s) => s.id)), [localSelections]);

  useEffect(() => {
    const filteredSelections = localSelections.filter((s) => matchesGroup(s.group));

    // Show selected items when input is empty
    if (!inputValue.trim()) {
      if (filteredSelections.length > 0) {
        setSearchResults([{ label: '', options: filteredSelections }]);
        setShowResults(true);
      } else {
        setSearchResults([]);
        setShowResults(false);
      }
      setFocusedIndex(-1);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setShowResults(true);

    searchIdRef.current += 1;
    const currentSearchId = searchIdRef.current;

    debouncedSearch(inputValue, currentSearchId);

    return () => {
      debouncedSearch.cancel();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputValue, debouncedSearch, matchesGroup]);

  useEffect(() => {
    if (
      isDisclosureOpen &&
      defaultQuery !== undefined &&
      !inputValue.trim() &&
      searchResults.length === 0
    ) {
      setIsLoading(true);
      setShowResults(true);
      searchFunction(defaultQuery)
        .then((results) => {
          if (!results || results.length === 0) {
            setSearchResults([]);
          } else {
            // Check if there are selected items not in the default results
            const currentFilteredSelections = localSelections.filter((s) => matchesGroup(s.group));

            const selectedNotInResults = currentFilteredSelections.filter(
              (selected) =>
                !results.some((group) => group.options.some((opt) => opt.id === selected.id))
            );

            if (selectedNotInResults.length > 0) {
              // Show selected items that are not in default results at the top
              setSearchResults([{ label: '', options: selectedNotInResults }, ...results]);
            } else {
              setSearchResults(results);
            }
          }
          setIsLoading(false);
        })
        .catch(() => {
          setIsLoading(false);
        });
    }
  }, [
    isDisclosureOpen,
    defaultQuery,
    inputValue,
    searchResults.length,
    searchFunction,
    localSelections,
    matchesGroup,
  ]);

  const handleInputChange = (value: string) => {
    setInputValue(value);
    setShowResults(true);
    setFocusedIndex(-1);
  };

  const handleInputFocus = () => {
    if (!inputValue.trim()) {
      setShowResults(true);
    }
  };

  const handleInputBlur = () => {
    setTimeout(() => {
      if (
        !isSelecting &&
        !resultsRef.current?.contains(document.activeElement) &&
        !mouseDownOnResults.current
      ) {
        setFocusedIndex(-1);
      }
    }, 150);
  };

  const handleSelect = (option: SelectOption) => {
    setIsSelecting(true);
    setShowResults(true);

    const isSelected = localSelections.some((selection) => selection.id === option.id);
    const newSelections: SelectOption[] = isSelected
      ? localSelections.filter((selection) => selection.id !== option.id)
      : [...localSelections, option];

    setLocalSelections(newSelections);
    onSelectionsChange(newSelections);

    setFocusedIndex(-1);
    inputRef.current?.focus();

    setTimeout(() => {
      setIsSelecting(false);
    }, 300);
  };

  const handleResultsMouseDown = () => {
    mouseDownOnResults.current = true;
  };

  const handleResultsMouseUp = () => {
    setTimeout(() => {
      mouseDownOnResults.current = false;
    }, 100);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!showResults || flattenedResults.length === 0) {
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setFocusedIndex((prev) => (prev < flattenedResults.length - 1 ? prev + 1 : 0));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setFocusedIndex((prev) => (prev > 0 ? prev - 1 : flattenedResults.length - 1));
        break;
      case 'Enter':
        e.preventDefault();
        if (focusedIndex >= 0 && flattenedResults[focusedIndex]) {
          handleSelect(flattenedResults[focusedIndex]);
        }
        break;
      case 'Escape':
        setShowResults(false);
        setFocusedIndex(-1);
        inputRef.current?.blur();
        break;
    }
  };

  return {
    searchResults,
    isLoading,
    inputValue,
    showResults,
    focusedIndex,
    localSelections,
    selectedIdSet,
    flattenedResults,

    inputRef,
    resultsRef,

    handleInputChange,
    handleInputFocus,
    handleInputBlur,
    handleSelect,
    handleResultsMouseDown,
    handleResultsMouseUp,
    handleKeyDown,
  };
}
